import {
  USER_LOADING,
  USER_LOADED,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGOUT_SUCCESS,
  REGISTER_SUCCESS,
  REGISTER_FAIL,
} from "../types/authTypes";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/";

const buildApiUrl = (path) => {
  const baseUrl = API_URL.endsWith("/") ? API_URL : `${API_URL}/`;
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;

  return `${baseUrl}${cleanPath}`;
};

const jsonConfig = {
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
};

const isHtmlResponse = (value) => {
  const text = String(value || "").trim().toLowerCase();

  return (
    text.startsWith("<!doctype html") ||
    text.startsWith("<html") ||
    text.includes("<body") ||
    text.includes("<title>403 forbidden</title>") ||
    text.includes("csrf verification failed") ||
    text.includes("origin checking failed")
  );
};

const friendlyErrorText = (message, status) => {
  const text = String(message || "").trim();
  const lowered = text.toLowerCase();

  if (isHtmlResponse(text)) {
    if (
      lowered.includes("csrf verification failed") ||
      lowered.includes("origin checking failed")
    ) {
      return "Login request was blocked by the server. Please refresh the page and try again.";
    }

    if (status === 403) {
      return "You do not have permission to perform this action. Please refresh the page and try again.";
    }

    return "Something went wrong on the server. Please try again.";
  }

  if (status === 403) {
    return "Login request was blocked by the server. Please refresh the page and try again.";
  }

  if (
    status === 404 ||
    lowered.includes("user not found") ||
    lowered.includes("could not find an account")
  ) {
    return "We couldn't find an account with those details.";
  }

  if (
    lowered.includes("invalid password") ||
    lowered.includes("password you entered is incorrect") ||
    lowered.includes("incorrect password")
  ) {
    return "The password you entered is incorrect.";
  }

  if (lowered.includes("email/username") && lowered.includes("password")) {
    return "Enter your email/username and password to continue.";
  }

  if (lowered.includes("invalid role")) {
    return "Please select a valid account type.";
  }

  if (
    lowered.includes("network") ||
    lowered.includes("network error") ||
    lowered.includes("failed to fetch")
  ) {
    return "Network or server error. Please check your connection and try again.";
  }

  return text || "Something went wrong. Please try again.";
};

const formatFieldName = (field) =>
  String(field || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeFieldErrors = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return {};
  }

  return Object.entries(data).reduce((acc, [field, value]) => {
    if (
      ["error", "detail", "message", "available_roles", "success", "user"].includes(
        field
      )
    ) {
      return acc;
    }

    if (Array.isArray(value)) {
      acc[field] = value.join(" ");
    } else if (typeof value === "string") {
      acc[field] = value;
    } else if (value && typeof value === "object") {
      acc[field] = Object.values(value).flat().join(" ");
    }

    return acc;
  }, {});
};

const extractErrorDetails = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const fallback = status ? "Request failed" : "Network or server error";

  if (!data) {
    return {
      message: friendlyErrorText(fallback, status),
      fieldErrors: {},
    };
  }

  if (typeof data === "string") {
    return {
      message: friendlyErrorText(data, status),
      fieldErrors: {},
    };
  }

  const fieldErrors = normalizeFieldErrors(data);
  let message = data.error || data.detail || data.message;

  if (!message && Array.isArray(data.available_roles) && data.available_roles.length) {
    message = "Please select a valid account type.";
  }

  if (!message && Object.keys(fieldErrors).length) {
    const firstField = Object.keys(fieldErrors)[0];
    message = `${formatFieldName(firstField)}: ${fieldErrors[firstField]}`;
  }

  return {
    message: friendlyErrorText(message || fallback, status),
    fieldErrors,
  };
};

const extractErrorMessage = (err) => extractErrorDetails(err).message;

export const loadUser = () => async (dispatch) => {
  dispatch({ type: USER_LOADING });

  try {
    const res = await axios.get(buildApiUrl("api/user/"), jsonConfig);
    const user = res.data;

    dispatch({
      type: USER_LOADED,
      payload: { ...user, role: user.role },
    });

    return {
      ok: true,
      data: user,
    };
  } catch (err) {
    return {
      ok: false,
      error: extractErrorMessage(err),
    };
  }
};

export const register = (data) => async (dispatch) => {
  try {
    const registrationResponse = await axios.post(
      buildApiUrl("api/user/"),
      data,
      jsonConfig
    );

    const userId = registrationResponse.data?.id;

    if (userId) {
      try {
        await axios.post(
          buildApiUrl("api/cart/"),
          { userId, quantity: 0 },
          jsonConfig
        );
      } catch (cartErr) {
        console.warn("Signup succeeded, but cart initialization failed.", cartErr);
      }
    }

    dispatch({
      type: REGISTER_SUCCESS,
      payload: { ...data, id: userId, role_id: data.role_id },
    });

    return {
      ok: true,
      id: userId,
    };
  } catch (err) {
    const details = extractErrorDetails(err);

    dispatch({
      type: REGISTER_FAIL,
      msg: details.message,
    });

    return {
      ok: false,
      error: details.message,
      fieldErrors: details.fieldErrors,
    };
  }
};

export const login = (email, password) => async (dispatch) => {
  const body = {
    email,
    password,
  };

  try {
    const res = await axios.post(buildApiUrl("api/login/"), body, jsonConfig);

    dispatch({
      type: LOGIN_SUCCESS,
      payload: res.data,
    });

    return {
      ok: true,
      data: res.data,
    };
  } catch (err) {
    const details = extractErrorDetails(err);

    dispatch({
      type: LOGIN_FAIL,
      msg: details.message,
    });

    return {
      ok: false,
      error: details.message,
      fieldErrors: details.fieldErrors,
    };
  }
};

export const logout = () => async (dispatch) => {
  dispatch({
    type: LOGOUT_SUCCESS,
  });
};